/**
 * Keeps a browser translator from crashing the page.
 *
 * Google Translate, and the in-page translators that work the same way, wrap
 * text in `<font>` elements. React still believes the original parent owns
 * that text, so the next commit calls `removeChild` or `insertBefore` and the
 * DOM throws NotFoundError. The stack is entirely inside React, and the
 * visitor gets the crash screen.
 *
 * When the node is not a child of the parent React passed, skip that one call
 * and warn once. A matching parent uses the original method, so an
 * untranslated page is unchanged. Safe to call more than once, and a no-op
 * outside the browser.
 */

const PATCHED = Symbol.for('supplyautomate.translated-dom-guard.patched');
const WARNED = Symbol.for('supplyautomate.translated-dom-guard.warned');

/** Wrappers in this module, for a host that refuses a property on the function. */
const patchedFns = new WeakSet<object>();

interface GuardedPrototype {
  removeChild<T extends Node>(child: T): T;
  insertBefore<T extends Node>(node: T, child: Node | null): T;
}

function isPatched(fn: unknown): boolean {
  return (
    typeof fn === 'function' &&
    (patchedFns.has(fn) || Boolean((fn as { [PATCHED]?: boolean })[PATCHED]))
  );
}

function markPatched<T extends object>(fn: T): T {
  patchedFns.add(fn);

  try {
    Object.defineProperty(fn, PATCHED, { value: true });
  } catch {
    /*
     * The WeakSet already records the wrapper. A host that refuses the
     * property must not stop the guard from being installed.
     */
  }

  return fn;
}

function warnOnce(
  prototype: object,
  operation: 'removeChild' | 'insertBefore',
) {
  try {
    const flagged = prototype as { [WARNED]?: boolean };

    if (flagged[WARNED]) return;

    Object.defineProperty(prototype, WARNED, { value: true });
    console.warn(
      `Skipped ${operation}: the node is not a child of this parent. A browser translator may have rewritten the page.`,
    );
  } catch {
    /*
     * A console that refuses to log, or a prototype that refuses a new
     * property, must not turn the skipped call into the crash this guard
     * exists to prevent.
     */
  }
}

/**
 * Installs the parent-mismatch guard on `Node.prototype`.
 *
 * Idempotent: a method this function already wrapped is left as it is, so a
 * second call does not stack wrappers. If something later replaces a method,
 * calling this again wraps the replacement.
 */
export function installTranslatedDomGuard(): void {
  if (typeof window === 'undefined' || typeof Node !== 'function') {
    return;
  }

  const prototype = Node.prototype as unknown as GuardedPrototype;

  if (!isPatched(prototype.removeChild)) {
    const originalRemoveChild = prototype.removeChild;

    prototype.removeChild = markPatched(function removeChild<T extends Node>(
      this: Node,
      child: T,
    ): T {
      if (child && child.parentNode !== this) {
        warnOnce(prototype, 'removeChild');

        return child;
      }

      return originalRemoveChild.call(this, child) as T;
    });
  }

  if (!isPatched(prototype.insertBefore)) {
    const originalInsertBefore = prototype.insertBefore;

    prototype.insertBefore = markPatched(function insertBefore<T extends Node>(
      this: Node,
      node: T,
      child: Node | null,
    ): T {
      if (child && child.parentNode !== this) {
        warnOnce(prototype, 'insertBefore');

        return node;
      }

      return originalInsertBefore.call(this, node, child) as T;
    });
  }
}
